import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from '@supabase/supabase-js';
import { DEMO_AUTH } from '../config';
import { clearDemoData, type SavedArtist } from './savedRepo';
import { readStored, removeStored, writeStored } from './storage';
import { getSupabase, SUPABASE_ENABLED } from './supabase';

export type AuthUser = { id: string; email: string; name: string | null; avatarUrl: string | null };

/** An artist the visitor tried to add while signed out; saved once sign-in completes (FR-U4). */
export type PendingAdd = Pick<SavedArtist, 'id' | 'name' | 'imageUrl'>;

type SignInRequest = { pending?: PendingAdd };

type AuthContextValue = {
  user: AuthUser | null;
  /** True until the stored session (or an OAuth redirect) has been read. */
  loading: boolean;
  mode: 'supabase' | 'demo' | 'off';
  error: string | null;
  clearError: () => void;
  signInOpen: SignInRequest | null;
  openSignIn: (req?: SignInRequest) => void;
  closeSignIn: () => void;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  takePendingAdd: () => PendingAdd | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const PENDING_KEY = 'pendingAdd.v1';
const PENDING_MAX_AGE_MS = 30 * 60_000;
const DEMO_USER_KEY = 'demoUser.v1';
const DEMO_USER: AuthUser = { id: 'demo-user', email: 'demo@fixed.test', name: 'Demo listener', avatarUrl: null };

type StoredPending = PendingAdd & { at: number };
const isPending = (v: unknown): v is StoredPending =>
  !!v && typeof v === 'object' && typeof (v as StoredPending).id === 'string' && typeof (v as StoredPending).at === 'number';
const isDemoUser = (v: unknown): v is AuthUser => !!v && typeof v === 'object' && (v as AuthUser).id === DEMO_USER.id;

function toAuthUser(u: User): AuthUser {
  const meta = u.user_metadata as { full_name?: string; name?: string; avatar_url?: string; picture?: string };
  return {
    id: u.id,
    email: u.email ?? '',
    name: meta.full_name ?? meta.name ?? null,
    avatarUrl: meta.avatar_url ?? meta.picture ?? null,
  };
}

/** Friendly text for errors Supabase/Google hand back on the redirect URL (FR-U6). */
function readRedirectError(): string | null {
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.slice(1));
  const code = url.searchParams.get('error') ?? hash.get('error');
  if (!code) return null;
  const desc = url.searchParams.get('error_description') ?? hash.get('error_description') ?? '';
  for (const k of ['error', 'error_code', 'error_description']) url.searchParams.delete(k);
  window.history.replaceState(window.history.state, '', url.pathname + url.search);
  if (code === 'access_denied') return 'Sign-in was cancelled. You’re still signed out.';
  return desc ? `Sign-in didn’t work: ${desc}` : 'Sign-in didn’t work. Please try again.';
}

// Read once at startup: the redirect error lives in the URL we're about to clean.
// /callback is the Spotify redirect URI page; its ?error= belongs to Spotify, not to our sign-in.
const REDIRECT_ERROR =
  typeof window === 'undefined' || window.location.pathname === '/callback' ? null : readRedirectError();
if (REDIRECT_ERROR) removeStored(PENDING_KEY);

export function AuthProvider({ children }: { children: ReactNode }) {
  const mode: AuthContextValue['mode'] = SUPABASE_ENABLED ? 'supabase' : DEMO_AUTH ? 'demo' : 'off';
  const [user, setUser] = useState<AuthUser | null>(() =>
    mode === 'demo' ? readStored<AuthUser | null>(DEMO_USER_KEY, null, (v): v is AuthUser | null => v === null || isDemoUser(v)) : null,
  );
  const [loading, setLoading] = useState(mode === 'supabase');
  const [error, setError] = useState<string | null>(REDIRECT_ERROR);
  // A failed redirect reopens the dialog so the message shows where the user left off.
  const [signInOpen, setSignInOpen] = useState<SignInRequest | null>(REDIRECT_ERROR ? {} : null);
  const qc = useQueryClient();
  // Drop per-user cached data so the next account never sees the previous one's list.
  const forgetUserData = useCallback(() => qc.removeQueries({ queryKey: ['saved'] }), [qc]);

  useEffect(() => {
    if (!SUPABASE_ENABLED) return;
    let active = true;
    let unsubscribe = () => {};
    getSupabase()
      .then(async (sb) => {
        const { data: sub } = sb.auth.onAuthStateChange((_event, session) => {
          if (active) setUser(session ? toAuthUser(session.user) : null);
        });
        unsubscribe = () => sub.subscription.unsubscribe();
        const { data, error: e } = await sb.auth.getSession();
        if (!active) return;
        if (e) setError('Sign-in didn’t work. Please try again.');
        setUser(data.session ? toAuthUser(data.session.user) : null);
      })
      .catch(() => active && setError('Couldn’t load sign-in. Check your connection.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const openSignIn = useCallback((req: SignInRequest = {}) => {
    setError(null);
    setSignInOpen(req);
  }, []);
  const closeSignIn = useCallback(() => {
    setSignInOpen(null);
    removeStored(PENDING_KEY);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    if (signInOpen?.pending) writeStored(PENDING_KEY, { ...signInOpen.pending, at: Date.now() });
    if (mode === 'demo') {
      writeStored(DEMO_USER_KEY, DEMO_USER);
      setUser(DEMO_USER);
      setSignInOpen(null);
      return;
    }
    if (mode !== 'supabase') return;
    const supabase = await getSupabase();
    // Full-page redirect: no popup to block, works on iOS Safari (FR-U2).
    const back = new URL(window.location.href);
    back.hash = '';
    const { error: e } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: back.toString(), scopes: 'openid email profile', queryParams: { prompt: 'select_account' } },
    });
    if (e) {
      removeStored(PENDING_KEY);
      setError(navigator.onLine ? 'Couldn’t reach Google. Please try again.' : 'You appear to be offline.');
    }
  }, [mode, signInOpen]);

  const signOut = useCallback(async () => {
    if (mode === 'demo') removeStored(DEMO_USER_KEY);
    else if (mode === 'supabase') await (await getSupabase()).auth.signOut();
    setUser(null);
    forgetUserData();
  }, [mode, forgetUserData]);

  const deleteAccount = useCallback(async () => {
    if (mode === 'demo') {
      if (user) clearDemoData(user.id);
      removeStored(DEMO_USER_KEY);
      setUser(null);
      forgetUserData();
      return;
    }
    if (mode !== 'supabase') return;
    const supabase = await getSupabase();
    // Deletes the auth user; saved_artists rows go with it (ON DELETE CASCADE).
    const { error: e } = await supabase.rpc('delete_my_account');
    if (e) throw e;
    await supabase.auth.signOut({ scope: 'local' });
    setUser(null);
    forgetUserData();
  }, [mode, user, forgetUserData]);

  const takePendingAdd = useCallback((): PendingAdd | null => {
    const p = readStored<StoredPending | null>(PENDING_KEY, null, (v): v is StoredPending | null => v === null || isPending(v));
    removeStored(PENDING_KEY);
    if (!p || Date.now() - p.at > PENDING_MAX_AGE_MS) return null;
    return { id: p.id, name: p.name, imageUrl: p.imageUrl };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      mode,
      error,
      clearError: () => setError(null),
      signInOpen,
      openSignIn,
      closeSignIn,
      signInWithGoogle,
      signOut,
      deleteAccount,
      takePendingAdd,
    }),
    [user, loading, mode, error, signInOpen, openSignIn, closeSignIn, signInWithGoogle, signOut, deleteAccount, takePendingAdd],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
